import { Layout } from '@components/common'
import type {
    GetStaticProps,
    GetStaticPropsContext,
    InferGetStaticPropsType
} from 'next'
import { useUI } from '@corratech/pylot-ui/context'
import { NotFound } from '@components/404'
import getCategory from '@pylot-data/api/operations/get-category'
import getMarketingTiles from '@pylot-data/api/operations/get-marketing-tiles'
import {
    ProductItem,
    LoaderTile
} from '@components/common/ProductTile/ProductTile'
import {
    Breadcrumbs,
    CategoryBreadcrumb
} from '@components/alfakher/pylot-breadcrumbs'
import { RecentlyViewed } from '@corratech/pylot-recently-viewed'
import { PLPGrid } from '@corratech/pylot-plp-grid'
import { useCategory } from '@pylot-data/hooks/category/use-category'
import type { ProductInterface, Maybe } from '@pylot-data/pylotschema'
import { useLoadMoreButton } from '@lib/hooks/useLoadMoreButton'
import cn from 'classnames'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Button } from '@corratech/pylot-ui'
import { usePLPManager } from '@corratech/pylot-filters-and-sort'
import { usePlpProducts } from '@pylot-data/hooks/category/use-plp-products'
import React, { useEffect, useMemo, useRef } from 'react'
import s from '@ooka_uae_store/styles/page-styles/Plp.module.scss'
import { getSubcategories } from '@pylot-data/api/operations/get-subcategories'
import { getStoreConfig } from '@config/index'
import { usePlpTracking } from '@components/OOKA/pylot-tag-manager'
import { useTopNavTracking } from '@corratech/pylot-tag-manager'

import { useRouter } from 'next/router'
import { SEO, SEO_ENUMS } from '@components/seo'
import { SlicknextCircle, SlickprevCircle } from '@components/icons'
import slickStyles from '@commonStyles/Slick.module.scss'
import { FloatingCartIcon } from '@components/FloatingCartIcon'
import { RichContent } from '@corratech/pylot-rich-content'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

export const PLP_PAGE_SIZE = 16
const DISPLAY_MODE_CLP = 'PAGE'
const DISPLAY_MODE_PLP_BANNER = 'PRODUCTS_AND_PAGE'

export const getStaticPaths = async ({ locales }: { locales: [string] }) => {
    try {
        const paths: {
            params: { url_path: (Maybe<string> | undefined)[] }
            locale: string
        }[] =
            (await locales?.reduce(
                async (
                    acc: Promise<
                        {
                            params: { url_path: (Maybe<string> | undefined)[] }
                            locale: string
                        }[]
                    >,
                    locale: string
                ) => {
                    if (locale !== 'default') {
                        const config = getStoreConfig(locale) // Pass locale as an argument
                        if (config.static.categories?.root) {
                            const categorySearch = await getSubcategories({
                                rootCategory: config.static.categories.root,
                                pageSize: config.static.categories.count,
                                currentPage: 1,
                                locale
                            })
                            const categoriesByLocale = categorySearch.map(
                                ({ url_path }) => {
                                    return {
                                        params: { url_path: [url_path] },
                                        locale
                                    }
                                }
                            )
                            return [...(await acc), ...categoriesByLocale]
                        }
                    }
                    return await acc
                },
                Promise.resolve([])
            )) ?? []
        return {
            // automatically generate paths for all the products
            paths,
            fallback: 'blocking'
        }
    } catch (e) {
        console.log('CATEGORY GETSTATICPATHS ERROR')
        console.log(e)
        return {
            paths: [],
            fallback: 'blocking'
        }
    }
}
export const getStaticProps: GetStaticProps = async ({
    params,
    locale
}: GetStaticPropsContext) => {
    const urlPathParts = params!.url_path as string[]
    const url_path = urlPathParts.join('/')

    try {
        const categoryData = await getCategory({
            pageSize: PLP_PAGE_SIZE,
            url_path,
            locale
        })

        const category = categoryData?.categories?.items?.[0] ?? null
        const categoryUid = category?.uid ?? ''

        const marketingTiles = await getMarketingTiles({
            category_uid: categoryUid
        })

        return {
            props: {
                url_path,
                categoryData: categoryData ?? null,
                categoryUid,
                marketingTiles,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'plp',
                    'wishlist'
                ]))
            },
            revalidate: 14400
        }
    } catch (e) {
        console.log('CATEGORY GETSTATICPROPS ERROR')
        return {
            props: {
                url_path,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'plp',
                    'wishlist'
                ]))
            },
            revalidate: 14400
        }
    }
}
export const WrappedProductItem = ({
    product
}: {
    product: ProductInterface
}): JSX.Element => {
    return (
        <ProductItem
            className="product-tile"
            product={product}
            imageParam={{ width: 500 }}
        />
    )
}

export default function PLP({
    categoryData: categoryInitialData,
    categoryUid,
    url_path,
    Store
}: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element {
    const { t } = useTranslation(['common', 'plp'])
    const config = getStoreConfig()
    const { LoadingIndicator } = useUI()
    const floatingButtonRef = useRef<HTMLDivElement>(null)
    // Note to project teams - you can remove the `isHorizontal` URL parameter option
    // by hardcoding isHorizontalFilterMenu either true or false.
    // This is only used for demo purposes in Pylot.
    const router = useRouter()
    const { setPageType } = useAlfakherContext()
    const { filter: filterParam } = router.query
    const isHorizontalFilterMenu = false

    const { productsResult: unfilteredProductResult } = useCategory(
        url_path,
        categoryUid,
        {
            pageSize: PLP_PAGE_SIZE,
            filter: {},
            sort: {},
            searchCriteria: []
        },
        {
            initialData: {
                data: { categories: categoryInitialData?.categories }
            },
            revalidateOnMount: true,
            revalidateOnFocus: false
        },
        {
            initialData: [{ data: categoryInitialData?.products }],
            revalidateOnMount: true,
            revalidateOnFocus: false,
            initialSize: 2
        },
        {
            headers: {
                Store
            }
        },
        !filterParam || filterParam.length === 0
    )

    const plpManager = usePLPManager({
        categoryUid,
        unfilteredAggregations: unfilteredProductResult?.aggregations,
        filtersVisible: false
    })

    useEffect(() => {
        // On un-mount (meaning, when user leaves the category page or switches to another one)
        // clear the PLP Manager state so that it's not persisted to the next PLP
        return () => plpManager.clearPLPState({})
        // Don't add plpManager to this effect dependency array or else an endless loop occurs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryUid])

    useEffect(() => {
        // set header as black when display mode is PAGE
        // context not getting updated on layout.
        localStorage.setItem('Category', category?.name ?? '')
        if (typeof window !== 'undefined') {
            const layoutClass = document.getElementById('layout-main')
            if (category?.display_mode === DISPLAY_MODE_CLP) {
                layoutClass?.classList.add('dark_header')
            }
            // remove black header on unmount
            return () => layoutClass?.classList.remove('dark_header')
        }
    }, [categoryUid])

    const {
        category,
        productItems,
        productsResult,
        error,
        productsSwrResult: { setSize },
        isLast,
        isLoading: isCategoryDataLoading
    } = useCategory(
        url_path,
        categoryUid,
        {
            pageSize: PLP_PAGE_SIZE,
            filter: plpManager.plpState.filters,
            sort: plpManager.plpState.sort,
            searchCriteria: plpManager.plpState.appliedSearchCritieria
        },
        {
            initialData: {
                data: { categories: categoryInitialData?.categories }
            },
            revalidateOnMount: true,
            revalidateOnFocus: false
        },
        {
            initialData: [
                { data: { products: categoryInitialData?.products } }
            ],
            revalidateOnMount: true,
            revalidateOnFocus: false,
            initialSize: 2
        },
        {
            headers: {
                Store
            }
        }
    )

    useEffect(() => {
        setPageType({
            transparentHeader: !!category?.is_transparent_header,
            isNicotinePage: !!category?.has_nicotine
        })
    }, [category])

    const { isLoading: isLoadMoreLoading, onLoadMoreClick } = useLoadMoreButton(
        isCategoryDataLoading,
        setSize
    )

    const { visibleProductData, productsToRender } = usePlpProducts(
        isCategoryDataLoading,
        plpManager,
        productItems,
        categoryInitialData?.products,
        productsResult
    )

    const isLoadMoreHidden = isLast || productsToRender.length === 0

    const processImageDisruptor = () => {
        if (category?.image_disruptor) {
            try {
                const parsedData: Array<any> = Array.isArray(
                    category.image_disruptor
                )
                    ? category.image_disruptor
                    : JSON.parse(category.image_disruptor)

                return parsedData.map((item) => ({
                    recordId: item.record_id,
                    blockPosition: item.block_position,
                    blockId: item.block_id
                }))
            } catch (error) {
                console.error('Error parsing image_disruptor: ', error)
            }
            return []
        }
    }

    const plpGrid = useMemo(
        () => (
            <PLPGrid
                // temporarily disabling marketing tiles
                // marketingTiles={marketingTiles}
                marketingTiles={[]}
                ProductTile={WrappedProductItem}
                LoadingTile={LoaderTile}
                products={productsToRender}
                imageDisruptorData={processImageDisruptor()}
                isLoading={isCategoryDataLoading}
                pageSize={PLP_PAGE_SIZE}
            />
        ),
        [productsToRender]
    )

    usePlpTracking(category, isCategoryDataLoading, visibleProductData)

    const trackMenuClick = useTopNavTracking()

    const tabsContainerRef = useRef<HTMLDivElement>(null)
    const pendingClickTextRef = useRef<string | null>(null)

    const handleCaptureClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        const btn = target.closest('button')
        pendingClickTextRef.current = btn?.innerText?.trim() || null
    }

    useEffect(() => {
        const div = tabsContainerRef.current
        if (!div) return
        div.addEventListener('click', handleCaptureClick, true)
        return () => div.removeEventListener('click', handleCaptureClick, true)
    }, [category?.alfa_description])

    useEffect(() => {
        router.events.on('routeChangeStart', handleRouteChange)
        return () => {
            router.events.off('routeChangeStart', handleRouteChange)
        }
    }, [router.events, trackMenuClick])

    const handleRouteChange = (url: string) => {
        const text = pendingClickTextRef.current
        pendingClickTextRef.current = null
        if (!text) return
        const segments = url.match(/\/c\/([^?#]+)/)?.[1]?.split('/') || []
        if (segments.length >= 1) {
            trackMenuClick(text, url, segments)
        }
    }

    //route fallback
    if (router.isFallback) {
        return (
            <div className="p-10 mx-auto max-w-ooka">
                {t('Page for this product was not generated beforehand.')}
                <br />
                {t('Loading...')}
            </div>
        )
    }

    if (!category || error) {
        return <NotFound identifier={config.base.route?.cms_no_route!} />
    }

    const renderScripts = () => {
        return (
            <>
                {productsToRender && (
                    <SEO
                        category={category}
                        page={SEO_ENUMS.PLP}
                        products={productsToRender as ProductInterface[]}
                    />
                )}
            </>
        )
    }

    const renderBreadCrumbs = () => {
        return (
            <div className={s['breadcrumb-wrapper']}>
                <Breadcrumbs
                    category={{
                        name: category.name!,
                        breadcrumbs: category.breadcrumbs as CategoryBreadcrumb[]
                    }}
                    rootText={t('breadcrumbs|Home')}
                    separator=" / "
                    urlPrefix={t('/c')}
                />
            </div>
        )
    }

    const renderPlpGrid = () => {
        return (
            <div>
                <div className={s['product-list-wrap']}>{plpGrid}</div>
                <div className={s['load-more-wrapper']}>
                    <Button
                        variant="primary"
                        className={cn(s['load-more'], {
                            [s['load-more-hidden']]: isLoadMoreHidden
                        })}
                        onClick={onLoadMoreClick}
                    >
                        {isLoadMoreLoading ? t('LOADING...') : t('LOAD MORE')}
                    </Button>
                </div>
            </div>
        )
    }

    if (category.display_mode === DISPLAY_MODE_CLP) {
        return (
            <div className={cn(slickStyles.root)}>
                {renderScripts()}
                <div className={cn(s['plp-top-info'])} ref={floatingButtonRef}>
                    {category?.description && (
                        <RichContent
                            html={category?.description}
                            ProductTile={ProductItem}
                        />
                    )}
                </div>
                <div className={s['main']}>
                    {category?.alfa_description && (
                        <RichContent
                            html={category?.alfa_description}
                            ProductTile={ProductItem}
                        />
                    )}
                </div>
            </div>
        )
    }

    return (
        <div>
            {renderScripts()}
            <FloatingCartIcon />
            {category.display_mode === DISPLAY_MODE_PLP_BANNER && (
                <div className={s['plp-top-info']}>
                    {category?.description && (
                        <RichContent html={category?.description} />
                    )}
                </div>
            )}
            <div className={s['main']}>
                {renderBreadCrumbs()}
                <div className={s['header']}>
                    <h1>{category.name}</h1>
                </div>
                {category?.alfa_description && (
                    <div ref={tabsContainerRef} className={s['plp-tabs']}>
                        <RichContent
                            html={category?.alfa_description}
                            ProductTile={ProductItem}
                        />
                    </div>
                )}
                {productsToRender?.length > 0 && (
                    <div
                        className={cn(
                            s['product-listing-page'],
                            !isHorizontalFilterMenu && s['sidebar-filters']
                        )}
                    >
                        {renderPlpGrid()}
                    </div>
                )}
                <div className={cn(s['plp-bottom-info'])}>
                    {category?.category_banner && (
                        <RichContent
                            html={category?.category_banner}
                            ProductTile={ProductItem}
                        />
                    )}
                </div>
                <div
                    className={cn(
                        s['recently-viewed'],
                        'product-viewed',
                        slickStyles.root
                    )}
                >
                    <RecentlyViewed
                        className={s['recently-viewed-slider']}
                        ProductTile={WrappedProductItem}
                        sliderSettings={{
                            arrows: true,
                            speed: 400,
                            slidesToScroll: 4,
                            slidesToShow: 4,
                            draggable: true,
                            infinite: false,
                            dots: false,
                            fade: false,
                            prevArrow: (
                                <button type="button" className="slick-prev">
                                    <SlickprevCircle />
                                </button>
                            ),
                            nextArrow: (
                                <button type="button" className="slick-next">
                                    <SlicknextCircle />
                                </button>
                            ),
                            responsive: [
                                {
                                    breakpoint: 1023,
                                    settings: {
                                        slidesToScroll: 2,
                                        slidesToShow: 3
                                    }
                                },
                                {
                                    breakpoint: 767,
                                    settings: {
                                        slidesToScroll: 3,
                                        slidesToShow: 3,
                                        arrows: true
                                    }
                                },
                                {
                                    breakpoint: 480,
                                    settings: {
                                        slidesToScroll: 1,
                                        slidesToShow: 1,
                                        centerMode: true,
                                        centerPadding: '80px',
                                        arrows: false,
                                        dots: true
                                    }
                                }
                            ]
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

PLP.Layout = Layout
